/** @jsxImportSource @emotion/react */
"use client";
import { forwardRef, Fragment, type JSX } from "react";

import { radius } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { IconElement } from "../../icons/Icon/Icon";
import { ChevronRightIcon } from "../../icons/Icon/NavigationIcons";
import {
  baseStyle,
  contentStyle,
  disabledStyle,
  imageStyle as listImageStyle,
} from "../listStyles.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const imageStyle = [listImageStyle, { borderRadius: radius.full }];

export type ListCategoryProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  description?: string;
  disabled?: boolean;
  icon?: IconElement;
  imageSrc?: string;
  title: string;
};

export const ListCategory = forwardRef<any, ListCategoryProps>(
  (
    {
      asChild = false,
      children,
      description,
      disabled,
      icon = <ChevronRightIcon />,
      imageSrc,
      title,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <img alt={description} key="img" src={imageSrc} css={imageStyle} />,
        <span key="content" css={contentStyle}>
          <Text overflow="ellipsis" type="title">
            {title}
          </Text>
          {description && (
            <Text overflow="ellipsis" size="s" type="body">
              {description}
            </Text>
          )}
        </span>,
        <Fragment key="icon">{icon}</Fragment>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[baseStyle, disabled && disabledStyle]}
        {...{ disabled, ...props }}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ListCategory.displayName = "ListCategory";
